import React, { useEffect, useRef, useState } from "react";
import mapboxgl, { Map } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import somaliaGeoJSON from "../assets/map_updated.geojson";
import { motion, AnimatePresence } from "framer-motion";
import IncidenceByRiskLevel from "../components/charts/IncidenceByRiskLevel";


type RegionData = {
  district_name: string;
  risk_level: string;
  total_evictions: number;
  total_households: number;
  affected_areas: string[]; // Array of affected areas
};

mapboxgl.accessToken = "pk.eyJ1IjoiZ2Jpa2V0aSIsImEiOiJjbTdvazduMnIwOG0xMmpzODdieXBmeDlxIn0.BlMWq3YWnnOjbYfbCXCKIA";

const RiskMap: React.FC = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<Map | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedRegionData, setSelectedRegionData] = useState<RegionData | null>(null);
  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/streets-v11");
  const [districtTotals, setDistrictTotals] = useState<Record<string, number>>({});
  const [totalHouseholds, setTotalHouseholds] = useState<Record<string, number>>({});
  const [selectedRisk, setSelectedRisk] = useState<string | null>(null);
  const [evictionStats, setEvictionStats] = useState<Record<
    string,
    { districts: number; individuals: number; districtNames: string[] }
  > | null>(null);
  const [selectedEvictionStats, setSelectedEvictionStats] = useState<{ districts: number; individuals: number } | null>(null);
  const [leftData, setLeftData] = useState<
    { id: number; title: React.ReactNode; value: React.ReactNode; color?: string }[]
  >([]);


  const [pieData, setPieData] = useState<{ labels: string[]; datasets: { data: number[]; backgroundColor: string[] }[] }>({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
      },
    ],
  });
  // Store computed stats in a state variable
  const [computedEvictionStats, setComputedEvictionStats] = useState<Record<string, { individuals: number }>>({});

  // Function to clear the selected data (hide the card)
  const clearSelection = () => {
    setSelectedRegionData(null);
  };

  const rightTableData = [
    { metric: "Low", color: "#008000" },
    { metric: "Medium", color: "#ffff00" },
    { metric: "High", color: "#ffa500" },
    { metric: "Extreme", color: "#ff0000" },
  ];

  const computeEvictionStat = () => {
    const stats: Record<string, { individuals: number }> = {};

    Object.entries(districtTotals).forEach(([district, count]) => {
      const riskLevel = getRiskLevel(count); // Ensure this function exists
      if (!stats[riskLevel]) {
        stats[riskLevel] = { individuals: 0 };
      }
      stats[riskLevel].individuals += count;
    });

    return stats;
  };


  useEffect(() => {
    setComputedEvictionStats(computeEvictionStat());
  }, [districtTotals]); // Updates when `districtTotals` changes

  useEffect(() => {
    fetch(`${BASE_URL}/prod-risk-mapper/`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        const mapping = data.evictionRiskMappingPerDistrict || {};

        const totals: Record<string, number> = {};
        const totals_hh: Record<string, number> = {};

        for (const district in mapping) {
          const districtData = mapping[district];
          totals[district] = districtData.total_individuals_at_risk || 0;
          totals_hh[district] = districtData.total_households_at_risk || 0;
        }

        setDistrictTotals(totals);
        setTotalHouseholds(totals_hh);
      })
      .catch((error) => {
        console.error("Error fetching eviction risk data:", error);
      });
  }, []);

  // ✅ Initialize Map AFTER Data is Loaded
  useEffect(() => {
    if (!mapContainerRef.current || Object.keys(districtTotals).length === 0) return;

    const mapInstance = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyle,
      center: [46.3257, 5],
      zoom: 4.8,
    });

    mapInstance.on("load", () => {
      mapInstance.addSource("somalia-regions", {
        type: "geojson",
        data: somaliaGeoJSON as any,
      }
      );

      mapInstance.addLayer({
        id: "region-layer",
        type: "fill",
        source: "somalia-regions",
        paint: { "fill-color": "transparent", "fill-opacity": 0.8 },
      });

      mapInstance.addLayer({
        id: "region-border",
        type: "line",
        source: "somalia-regions",
        paint: { "line-color": "#9CA3AF", "line-width": 0.5 },
      });

      mapInstance.on("click", "region-layer", (e) => {
        const regionName = e.features?.[0]?.properties?.shapeName;
        if (regionName) {
          setSelectedRegion(regionName);
          const totalIndividuals = districtTotals[regionName] || 0;
          const totalHH = totalHouseholds[regionName] || 0;
          const riskLevel = getRiskLevel(totalIndividuals);
          console.log(`Selected Region: ${regionName}, Total Individuals: ${totalIndividuals}`);


          setSelectedRegionData({
            district_name: regionName,
            risk_level: riskLevel,
            total_evictions: totalIndividuals,
            total_households: totalHH,
            affected_areas: ['', ''] // Array of affected areas
          })
        }
      });

      // ✅ Apply Colors Immediately After Load
      applyRegionColors(mapInstance);

      // ✅ Change cursor to pointer when hovering over regions
      mapInstance.on("mouseenter", "region-layer", () => {
        mapInstance.getCanvas().style.cursor = "pointer";
      });

      // ✅ Reset cursor when moving out of a region
      mapInstance.on("mouseleave", "region-layer", () => {
        mapInstance.getCanvas().style.cursor = "";
      });

      // ✅ Add Compass (NavigationControl with showCompass: true)
      const nav = new mapboxgl.NavigationControl({ showCompass: true });
      mapInstance.addControl(nav, "top-right");

      setMap(mapInstance);
    });

    return () => mapInstance.remove();
  }, [mapStyle, districtTotals]); // ✅ Map initializes only after data is loaded
  // ✅ Compute and Apply Colors Based on Population Count
  const riskColors: Record<string, string> = {
    "Extreme": "#ff0000",      // remains red
    "High": "#ffa500",         // orange
    "Medium": "#ffff00",       // yellow
    "Low": "#008000",          // green
  };

  const getRiskLevel = (count: number) => {
    if (count > 2500) return "Extreme";
    if (count > 1500) return "High";
    if (count > 1000) return "Medium";
    return "Low";
  };


  const riskDescriptions: Record<string, string> = {
    Low: "across Somalia face minimal or are slightly at risk of evictions because there exist agreements, due diligence was done before settlement, community is slightly welcoming etc.",
    Medium: "across Somalia, face significant risks of evictions because of existing factors such as disputes over the land, agreements have short period remaining etc.",
    High: "across Somalia, are high risk of eviction because most agreements are expired and most owners are not willing to extend, disputes on land, rental issues etc.",
    Extreme: "across Somalia, are extremely at risk of eviction because of various factors including but not limited to expired or no agreements, disputes on the land etc.",
  };


  const applyRegionColors = (mapInstance: mapboxgl.Map) => {
    mapInstance.setPaintProperty("region-layer", "fill-color", [
      "match",
      ["get", "shapeName"],
      ...Object.entries(districtTotals).flatMap(([district, count]) => [district, riskColors[getRiskLevel(count)]]),
      "#e7ebc9",
    ]);
  };

  // ✅ Compute Table Data Directly from districtTotals
  const filteredData = Object.entries(districtTotals)
    .map(([district, individuals]) => ({ district, individuals, risk: getRiskLevel(individuals) }))
    .filter(({ risk }) => !selectedRisk || risk === selectedRisk)
    .sort((a, b) => b.individuals - a.individuals)
    .slice(0, 5); // ✅ Always take top 5


  // ✅ Handle Clicking on a Risk Level in the Table
  const handleRiskClick = (riskLevel: string) => {
    console.log(`Clicked Risk Level: ${riskLevel}`);
    setSelectedRisk(riskLevel);

    if (map) {
      // Get matching districts for the selected risk level
      const matchingDistricts = Object.entries(districtTotals)
        .filter(([, count]) => getRiskLevel(count) === riskLevel)
        .map(([district]) => district);

      // If no matches, reset layer color to fallback only
      if (matchingDistricts.length === 0) {
        map.setPaintProperty("region-layer", "fill-color", "#e7ebc9");
      } else {
        // Otherwise, color only the matching districts
        map.setPaintProperty("region-layer", "fill-color", [
          "match",
          ["get", "shapeName"],
          ...matchingDistricts.flatMap((district) => [district, riskColors[riskLevel]]),
          "#e7ebc9", // fallback color for others
        ]);
      }
    }

    // ✅ Update selectedEvictionStats and leftData together
    if (evictionStats && evictionStats[riskLevel]) {
      const { districts, individuals, districtNames } = evictionStats[riskLevel];
      setSelectedEvictionStats(evictionStats[riskLevel]);

      setLeftData([
        {
          id: 1,
          title: `${riskLevel} Eviction Risk Overview`,
          value: (
            <>
              <span style={{ color: riskColors[riskLevel], fontWeight: "bold" }}>
                {individuals.toLocaleString()}
              </span>{" "}
              individuals in{" "}
              <span style={{ color: riskColors[riskLevel], fontWeight: "bold" }}>
                {districts}
              </span>{" "}
              district(s) <span>{riskDescriptions[riskLevel]}</span>
            </>
          ),
        },
        {
          id: 2,
          title: `District(s) at ${riskLevel} Risk of Eviction`,
          value: districtNames.length > 0 ? (
            districtNames.map((district, i) => (
              <span
                key={district}
                style={{ color: riskColors[riskLevel], fontWeight: "bold" }}
              >
                {district}
                {i < districtNames.length - 1 ? ", " : ""}
              </span>
            ))
          ) : (
            "No districts available."
          ),
        },
        { id: 3, title: "Recent Evictions", value: "949,404 individuals evicted since 2016." }
      ]);
    } else {
      setSelectedEvictionStats(null);
      setLeftData([
        { id: 1, title: `${riskLevel} Eviction Risk Overview`, value: `No eviction stats available for this risk level at the moment.` },
        { id: 2, title: `District(s) at ${riskLevel} Risk of Eviction`, value: "0" },
        { id: 3, title: "Recent Evictions", value: `48, 000 individuals were evicted recently.` },
      ]);
    }

    // ✅ Update Pie Chart to show only selected risk level data
    const riskCounts = rightTableData.map((row) =>
      row.metric === riskLevel
        ? Object.entries(districtTotals).reduce(
          (sum, [district, count]) => (getRiskLevel(count) === row.metric ? sum + count : sum),
          0
        )
        : 0
    );

    setPieData({
      labels: rightTableData.map((row) => row.metric),
      datasets: [
        {
          data: riskCounts,
          backgroundColor: rightTableData.map((row) => row.color),
        },
      ],
    });
  };

  const computeEvictionStats = (districtTotals: Record<string, number>) => {
    const stats: Record<string, { districts: number; individuals: number; districtNames: string[] }> = {};

    Object.entries(districtTotals).forEach(([district, count]) => {
      const riskLevel = getRiskLevel(count);

      if (!stats[riskLevel]) {
        stats[riskLevel] = { districts: 0, individuals: 0, districtNames: [] };
      }

      stats[riskLevel].districts += 1;
      stats[riskLevel].individuals += count;
      stats[riskLevel].districtNames.push(district); // Store district names
    });

    return stats;
  };



  useEffect(() => {
    if (districtTotals && Object.keys(districtTotals).length > 0) {
      setEvictionStats(computeEvictionStats(districtTotals));
    }
  }, [districtTotals]);

  useEffect(() => {
    if (evictionStats) {
      let totalIndividuals = 0;
      let totalDistricts = 0;

      Object.values(evictionStats).forEach((risk) => {
        totalIndividuals += risk.individuals;
        totalDistricts += risk.districts;
      });

      setLeftData([
        { id: 1, title: "Total Evictions", value: `${totalIndividuals.toLocaleString()} individuals in ${totalDistricts} districts across Somalia are at risk of eviction because of various factors including but not limited to expired or no agreements, disputes on the land etc.` },
        { id: 2, title: "Risk Level", value: `${totalIndividuals.toLocaleString()} individuals have been at risk of eviction over the past year.` },
        { id: 3, title: "Recent Evictions", value: `48, 000 individuals were evicted recently.` },
      ]);
    }
  }, [evictionStats]);


  useEffect(() => {
    if (!evictionStats || Object.keys(evictionStats).length === 0) return;

    // Extract risk labels (keys from evictionStats)
    const riskLabels = Object.keys(evictionStats);

    // Extract number of individuals affected (values from evictionStats)
    const riskValues = Object.values(evictionStats).map((stat) => stat.individuals);

    // Get colors for the risks
    const riskColors = riskLabels.map(
      (risk) => rightTableData.find((row) => row.metric === risk)?.color || "#d8d2cb"
    );

    // ✅ Update the chart data
    setPieData({
      labels: riskLabels,
      datasets: [
        {
          data: riskValues,
          backgroundColor: riskColors,
        },
      ],
    });
  }, [evictionStats]); // Runs when evictionStats changes


  useEffect(() => {
    if (selectedRegionData) {
      const timer = setTimeout(() => {
        clearSelection(); // Hide card after 60 seconds
      }, 10000);
      return () => clearTimeout(timer); // Cleanup timer when component re-renders
    }
  }, [selectedRegionData, clearSelection]);


  return (
    <div className="relative flex w-full h-[600px]">
      <div className="w-1/4 p-2 flex flex-col space-y-2">
        {leftData.map((item) => (
          <div key={item.id} className="bg-[#185076]/60 shadow-md rounded text-sm text-white backdrop-blur-md">
            <div className="p-3 font-semibold bg-[#185076]/80">{item.title}</div>
            <div className="p-3">{item.value}</div>
          </div>
        ))}
        <div className="bg-[#185076]/60 shadow-md rounded text-sm text-white max-h-[270px] backdrop-blur-md">
          <div className="p-3 font-semibold bg-[#185076]/80 sticky top-0 z-10 text-sm">
            How the Ranking is Done
          </div>
          <div className="p-3 overflow-y-auto max-h-[240px] text-sm">
            <p className="mb-2">
              Eviction risk severity is classified into five categories: extreme, high, medium, low and below.
              Each of these risk levels is determined by a set of predefined criteria resulting from existing factors on ground at the time of assessment.
            </p>
            <div className="mt-2">
              <p className="font-bold text-[#ff0000] text-base">Extreme</p>
              <p>
                Several factors can lead to evictions. For example, notices may be issued because rental agreements
                have expired and owners are unwilling to extend them, due to ongoing land disputes, or because of rental issues such as
                price hikes and widespread defaults. </p>
            </div>
            <div className="mt-2">
              <p className="font-bold text-[#fb923c] text-base">High</p>
              <p>There exist significant risks because there are factors in place such as No Agreements or expiring in less than 1 year and owners
                not showing interest to extend, disputes over land, policy or authority position etc .</p>
            </div>
            <div className="mt-2">
              <p className="font-bold text-[#facc15] text-base">Medium</p>
              <p>This represents an intermediate risk such as agreements expiring in &lt;1yr – possibility of renewal,
                but coming in more than one year, land is government authorized occupancy with no immediate plans etc.</p>
            </div>
            <div className="mt-2">
              <p className="font-bold text-[#008000] text-base">Low</p>
              <p>There exist agreements of more than 2 -5yrs, land ownership is clear with no history of disputes, community has better livelihoods to pay rent, if land belong to government there are no plans on it. </p>
            </div>

          </div>
        </div>


      </div>

      <div className="w-1/2 relative">
        {/* Map Container */}
        <div ref={mapContainerRef} className="absolute top-0 left-0 w-full h-full" />

        {/* Map Style Buttons */}
        <div className="absolute top-2 left-2 flex space-x-2">
          <button onClick={() => setMapStyle("mapbox://styles/mapbox/dark-v10")} className="p-2 bg-gray-800 text-white rounded">Dark Mode</button>
          <button onClick={() => setMapStyle("mapbox://styles/mapbox/light-v10")} className="p-2 bg-gray-200 text-black rounded">Light Mode</button>
          <button onClick={() => setMapStyle("mapbox://styles/mapbox/satellite-v9")} className="p-2 bg-gray-500 text-white rounded">Satellite</button>
          <button onClick={() => setMapStyle("mapbox://styles/mapbox/outdoors-v11")} className="p-2 bg-green-700 text-white rounded">Outdoors</button>
        </div>

        {/* Floating Card - Bottom Right */}
        <AnimatePresence mode="popLayout">
          {selectedRegionData && (
            <motion.div
              key={selectedRegionData.district_name} // Re-trigger animation on change
              initial={{ opacity: 0, scale: 0.8, x: 50 }} // Start small and transparent
              animate={{ opacity: 1, scale: 1, x: 0 }} // Grow to full size
              exit={{ opacity: 0, scale: 0.8, x: -50 }} // Shrink and fade when disappearing
              transition={{
                type: "spring",
                stiffness: 120,
                damping: 15,
                duration: 0.1,
              }}
              className="absolute bottom-8 right-4 bg-gradient-to-r from-blue-500 to-blue-700 bg-opacity-90 text-white shadow-xl rounded-lg p-2.5 w-52 backdrop-blur-md border border-white/30"
            >
              <h3 className="text-xs font-semibold mb-1">Eviction Risk Data Overview</h3>

              {/* Table */}
              <div className="w-full text-xs">
                <div className="grid grid-cols-2 bg-[#0C4767]/60 text-white font-semibold p-1 rounded-t-lg">
                  <div className="px-2">Category</div>
                  <div className="px-2 text-right">Value</div>
                </div>

                {[
                  ["District Name:", selectedRegionData?.district_name || "N/A"],
                  ["Risk Level:", selectedRegionData?.risk_level || "N/A"],
                  ["Total Evictions:", selectedRegionData?.total_evictions?.toLocaleString() || "0"],
                  ["Total Households:", selectedRegionData?.total_households?.toLocaleString() || "0"],
                  [
                    "Affected Areas",
                    Array.isArray(selectedRegionData?.affected_areas)
                      ? selectedRegionData.affected_areas.join(", ")
                      : "N/A",
                  ],
                ].map((item, index) => (
                  <div
                    key={index}
                    className={`grid grid-cols-2 px-2 py-1 ${index % 2 === 0
                      ? "bg-[#0C4767]/40 backdrop-blur-md" // Darker blue for contrast
                      : "bg-[#0C4767]/20 backdrop-blur-md" // Lighter blue for balance
                      } rounded-b-lg transition-all duration-300`}
                  >
                    <div className="text-white/95 font-medium">{item[0]}</div>
                    <div className="text-right text-white/90 font-semibold">{item[1]}</div>
                  </div>
                ))}
              </div>

            </motion.div>
          )}
        </AnimatePresence>

      </div>


      <div className="w-1/4 p-2 flex flex-col space-y-2">
        <div className="bg-[#185076]/60 shadow-md rounded text-sm text-white p-3 text-center font-semibold backdrop-blur-md">
          Eviction Risk Analysis – Last One Year
        </div>
        <div className="bg-[#185076]/60 shadow-md rounded text-sm text-white">
          <div className="p-3 font-semibold bg-[#185076]/80">Risk levels</div>
          <table className="w-full text-sm">
            <tbody>
              <tr>
                {rightTableData.map((row, index) => (
                  <td
                    key={index}
                    className={`p-2 w-1/5 h-10 cursor-pointer border border-white/20 ${selectedRisk === row.metric ? "ring-2 ring-white" : ""
                      }`}
                    style={{ backgroundColor: row.color }}
                    onClick={() => handleRiskClick(row.metric)}
                  ></td>
                ))}
              </tr>
            </tbody>
          </table>
          {/* Pie Chart Below the Table */}
          <div className="bg-[#185076]/60 shadow-md rounded text-sm text-white backdrop-blur-md w-full">
            {/* Left-Aligned Title */}
            <div className="font-semibold bg-[#185076]/80 w-full p-3">
              Risk Distribution
            </div>

            {/* Centered Chart */}
            <div className="flex justify-center p-3">
              <div className="w-48 h-48">
                <IncidenceByRiskLevel evictionStats={computedEvictionStats} />
              </div>
            </div>
          </div>

        </div>
        <div className="bg-[#185076]/60 shadow-md rounded text-sm text-white">
          <div className="p-3 font-semibold bg-[#185076]/80">Top 5 Districts on Risk Levels</div>
          <table className="w-full text-xs">
            <tbody>
              {filteredData.map((row, index) => (
                <tr key={index} className="border-b border-white/20">
                  <td className="p-2">{row.district}</td>
                  <td className="p-2">{row.individuals.toLocaleString()}</td>
                  <td className="p-2">{row.risk}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-[#185076]/60 shadow-md rounded text-sm text-white backdrop-blur-md">
          <div className="p-3 font-semibold bg-[#185076]/80 sticky top-0 z-10 text-base">
            Health Facilities
          </div>
          <div className="p-3 overflow-y-auto max-h-[220px] text-sm">
            <p className="whitespace-pre-line">
              {/*
                In December 2022, 17,192 individuals were forcefully evicted across Somalia, 
                bringing the total to 188,186 forced evictions since January 2022.
                Newly displaced families are joining existing IDP settlements as a coping mechanism 
                with unclear tenure arrangements, putting them at further risk of forced evictions. 
                Significant secondary displacements and forced evictions are expected to persist in 
                the coming months due to an influx of new arrivals in search of humanitarian assistance 
                and tenure insecurity.
            */}
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default RiskMap;